import { Fragment, useEffect, useMemo, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSCheckBox from "views/system-design/DSCheckBox";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSSlider from "views/system-design/DSSlider";
import DSSwitch from "views/system-design/DSSwitch";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSLinearProgress from "views/system-design/DSLinearProgress";
import firebaseService, {
  DeviceRunProgress,
} from "../../../firebase_service/firebaseService";
import { extractApiErrorMessage } from "../../../utils/util";
import {
  SYNCED_SETTINGS,
  SyncedSetting,
  SyncedSettingValue,
  TimeRange,
  storedValue,
  supportsSetting,
} from "../syncedSettings";

const BATCH_SETTINGS = SYNCED_SETTINGS.filter((setting) => !setting.noBatch);
const SECTIONS = Array.from(
  new Set(BATCH_SETTINGS.map((setting) => setting.section)),
);

const versionNote = (setting: SyncedSetting) =>
  [
    setting.minVersion !== undefined ? `≥ ${setting.minVersion}` : "",
    setting.belowVersion !== undefined ? `< ${setting.belowVersion}` : "",
  ]
    .filter(Boolean)
    .join(", ");

const SettingInput = ({
  setting,
  value,
  disabled,
  onChange,
}: {
  setting: SyncedSetting;
  value: SyncedSettingValue;
  disabled: boolean;
  onChange: (value: SyncedSettingValue) => void;
}) => {
  switch (setting.type) {
    case "enum":
      return (
        <DSTextField
          select
          size="small"
          sx={{ minWidth: "200px" }}
          disabled={disabled}
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
        >
          {setting.options.map((option) => (
            <DSMenuItem key={option} value={option}>
              {option}
            </DSMenuItem>
          ))}
        </DSTextField>
      );
    case "boolean":
      return (
        <DSSwitch
          disabled={disabled}
          checked={value as boolean}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case "number":
      return (
        <DSSlider
          sx={{ width: "200px" }}
          disabled={disabled}
          min={0}
          max={setting.max}
          valueLabelDisplay="auto"
          value={value as number}
          onChange={(_, next) => onChange(next as number)}
        />
      );
    case "timeRange": {
      const range = value as TimeRange;
      return (
        <DSBox display={"flex"} gap={1}>
          <DSTextField
            type="time"
            size="small"
            label="From"
            disabled={disabled}
            value={range.from}
            onChange={(e) => onChange({ ...range, from: e.target.value })}
          />
          <DSTextField
            type="time"
            size="small"
            label="Until"
            disabled={disabled}
            value={range.until}
            onChange={(e) => onChange({ ...range, until: e.target.value })}
          />
        </DSBox>
      );
    }
  }
};

type BatchSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  devices: { id: string; version?: number }[];
};

/**
 * Writes the checked synced settings to every selected device, the same
 * `{ time, value, source }` write the device modal does. A device whose
 * version doesn't run a setting (the modal's version gates) is skipped for
 * that setting. The write gates on each device's live version; the confirm
 * step's counts use the table rows' versions, so they can be slightly off.
 */
const BatchSettingsDialog = ({
  open,
  onClose,
  devices,
}: BatchSettingsDialogProps) => {
  // Only checked settings are in here; unchecked ones are left untouched.
  const [chosen, setChosen] = useState<Record<string, SyncedSettingValue>>({});
  const [lastValues, setLastValues] = useState<
    Record<string, SyncedSettingValue>
  >({});
  const [confirming, setConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState<DeviceRunProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setChosen({});
      setLastValues({});
      setConfirming(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  const chosenSettings = useMemo(
    () => BATCH_SETTINGS.filter((setting) => setting.field in chosen),
    [chosen],
  );

  const plan = useMemo(() => {
    const updates = devices.filter((device) =>
      chosenSettings.some((setting) =>
        supportsSetting(setting, device.version),
      ),
    );
    const skipped = chosenSettings
      .map((setting) => ({
        setting,
        count: devices.filter(
          (device) => !supportsSetting(setting, device.version),
        ).length,
      }))
      .filter(({ count }) => count > 0);
    return { updates, skipped };
  }, [devices, chosen, chosenSettings]);

  const toggle = (setting: SyncedSetting, checked: boolean) => {
    setConfirming(false);
    setChosen((prev) => {
      const next = { ...prev };
      if (checked) {
        next[setting.field] = lastValues[setting.field] ?? setting.default;
      } else {
        delete next[setting.field];
      }
      return next;
    });
  };

  const setValue = (setting: SyncedSetting, value: SyncedSettingValue) => {
    setConfirming(false);
    setChosen((prev) => ({ ...prev, [setting.field]: value }));
    setLastValues((prev) => ({ ...prev, [setting.field]: value }));
  };

  // A cleared time input reads "", which would be written to every device.
  const hasEmptyTime = chosenSettings.some((setting) => {
    if (setting.type !== "timeRange") return false;
    const range = chosen[setting.field] as TimeRange;
    return !range.from || !range.until;
  });

  const handleApply = async () => {
    setIsSaving(true);
    setProgress(null);
    setError(null);
    try {
      const { succeeded, failed, skipped } =
        await firebaseService.updateConfigsOnDevices(
          devices.map((device) => device.id),
          (version) =>
            Object.fromEntries(
              chosenSettings
                .filter((setting) => supportsSetting(setting, version))
                .map((setting) => [
                  setting.field,
                  storedValue(setting, chosen[setting.field], version),
                ]),
            ),
          setProgress,
        );
      setResult(
        `Updated ${succeeded.length} of ${devices.length} devices.` +
          (skipped.length
            ? ` ${skipped.length} skipped (their version runs none of these settings).`
            : "") +
          (failed.length
            ? ` ${failed.length} failed - you can safely retry.`
            : ""),
      );
    } catch (e) {
      setError(extractApiErrorMessage(e, "Failed to update the devices"));
    } finally {
      setIsSaving(false);
    }
  };

  const deviceCount = `${devices.length} device${devices.length === 1 ? "" : "s"}`;

  return (
    <DSDialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DSDialogTitle>Change settings</DSDialogTitle>
      <DSDialogContent>
        {result ? (
          <DSTypography variant="body2">{result}</DSTypography>
        ) : (
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}
          >
            <DSTypography variant="body2">
              {deviceCount} selected. Check the settings to change; the rest
              stay as they are on each device.
            </DSTypography>
            <DSBox
              sx={{
                display: "grid",
                gridTemplateColumns: "auto minmax(160px, auto) 1fr",
                alignItems: "center",
                columnGap: 1,
              }}
            >
              {SECTIONS.map((section) => (
                <Fragment key={section}>
                  <DSTypography
                    variant="subtitle2"
                    sx={{ gridColumn: "1 / -1", mt: 2, fontWeight: 700 }}
                  >
                    {section}
                  </DSTypography>
                  {BATCH_SETTINGS.filter(
                    (setting) => setting.section === section,
                  ).map((setting) => {
                    const checked = setting.field in chosen;
                    const note = versionNote(setting);
                    return (
                      <Fragment key={setting.field}>
                        <DSCheckBox
                          size="small"
                          checked={checked}
                          disabled={isSaving}
                          onChange={(e) => toggle(setting, e.target.checked)}
                        />
                        <DSTypography>
                          {setting.label}
                          {note && (
                            <DSTypography
                              variant="caption"
                              sx={{ color: "text.secondary", ml: 1 }}
                            >
                              ({note})
                            </DSTypography>
                          )}
                        </DSTypography>
                        <DSBox sx={{ py: 0.5 }}>
                          <SettingInput
                            setting={setting}
                            disabled={!checked || isSaving}
                            value={
                              chosen[setting.field] ??
                              lastValues[setting.field] ??
                              setting.default
                            }
                            onChange={(value) => setValue(setting, value)}
                          />
                        </DSBox>
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </DSBox>

            {confirming && (
              <DSBox
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <DSTypography variant="body2" sx={{ fontWeight: 700 }}>
                  Write {chosenSettings.length} setting
                  {chosenSettings.length === 1 ? "" : "s"} to{" "}
                  {plan.updates.length} of {deviceCount}?
                </DSTypography>
                {plan.skipped.map(({ setting, count }) => (
                  <DSTypography
                    key={setting.field}
                    variant="caption"
                    color="text.secondary"
                  >
                    {setting.label}: skipped on {count} device
                    {count === 1 ? "" : "s"} outside version{" "}
                    {versionNote(setting)}
                  </DSTypography>
                ))}
              </DSBox>
            )}

            {error && (
              <DSTypography variant="caption" color="error">
                {error}
              </DSTypography>
            )}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        {isSaving && progress && (
          <DSBox sx={{ flex: 1, mr: 2 }}>
            <DSLinearProgress
              variant="determinate"
              color={progress.retrying ? "warning" : "primary"}
              value={(progress.done * 100) / progress.total}
            />
            <DSTypography variant="caption" color="text.secondary">
              {progress.retrying ? "Retrying failed devices: " : "Updating "}
              {progress.done.toLocaleString()} of{" "}
              {progress.total.toLocaleString()} (
              {Math.floor((progress.done * 100) / progress.total)}%)
            </DSTypography>
          </DSBox>
        )}
        {result ? (
          <DSButton variant="contained" size="small" onClick={onClose}>
            Close
          </DSButton>
        ) : (
          <>
            <DSButton size="small" disabled={isSaving} onClick={onClose}>
              Cancel
            </DSButton>
            {confirming ? (
              <DSButton
                variant="contained"
                color="error"
                size="small"
                disabled={isSaving || plan.updates.length === 0}
                onClick={handleApply}
                startIcon={isSaving ? <DSCircularProgress size={14} /> : null}
              >
                Confirm
              </DSButton>
            ) : (
              <DSButton
                variant="contained"
                size="small"
                disabled={
                  chosenSettings.length === 0 ||
                  devices.length === 0 ||
                  hasEmptyTime
                }
                onClick={() => setConfirming(true)}
              >
                Apply to {deviceCount}
              </DSButton>
            )}
          </>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default BatchSettingsDialog;
